import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CatalogueCTA from "./CatalogueCTA";

/**
 * Ledger-style navbar - three lines, like the masthead of a magazine
 * spread. Year mark on the left, name + edition centred, a single nav
 * row below. No hamburger explosion, no glass-blur, no shadows.
 */
const NAV = [
  { id: "home",      label: "Home",          route: "/" },
  { id: "story",     label: "Our Story",     route: "/our-story" },
  { id: "catalogue", label: "View Catalogue", route: "/catalogue" },
  { id: "products",  label: "Our Products" },
  { id: "about",     label: "House"     },
  { id: "inquiry",   label: "Inquiry"   },
];

const TAGLINE_PRIMARY = "Connecting borders with confidence";
const TAGLINE_TRUST = "Vilora the synonym of trust";
const TAGLINE_SEQUENCE = [
  TAGLINE_PRIMARY,
  TAGLINE_TRUST,
  TAGLINE_PRIMARY,
];
const TAGLINE_LOOP = Array.from({ length: 5 }, () => TAGLINE_SEQUENCE).flat();

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const goHome = (event) => {
    event?.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 80);
      return;
    }
    scrollToId("top");
  };

  const go = (itemOrId) => {
    const item = typeof itemOrId === "string" ? { id: itemOrId } : itemOrId;
    setOpen(false);

    if (item.route) {
      navigate(item.route);
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 80);
      return;
    }

    if (location.pathname !== "/") {
      navigate({ pathname: "/", hash: `#${item.id}` });
      return;
    }

    scrollToId(item.id);
  };

  return (
    <header className="relative z-30 reveal reveal-d1" style={{ zIndex: open ? 90 : undefined }}>
      {/* Top line: looping house tagline */}
      <div className="navbar-tagline-strip" aria-label={`${TAGLINE_PRIMARY}. ${TAGLINE_TRUST}.`}>
        <div className="navbar-tagline-track" aria-hidden="true">
          {[...TAGLINE_LOOP, ...TAGLINE_LOOP].map((text, index) => (
            <span
              className={`navbar-tagline-item ${text === TAGLINE_TRUST ? "is-trust" : ""}`}
              key={`${text}-${index}`}
            >
              <span>{text}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Masthead - name centred, with two ornaments */}
      <div className="navbar-masthead px-6 sm:px-10 pt-7 pb-6 flex items-baseline justify-between gap-6">
        {/* Left ornament - actual house logo */}
        <a href="/" onClick={goHome} className="navbar-brand-lockup hidden sm:flex items-center gap-3 group">
          <img
            src="/logo.png"
            alt="Vilora Impex — Indian Trading House, Gujarat"
            width={128}
            height={128}
            className="navbar-house-mark object-contain transition-transform duration-500 group-hover:scale-105"
            style={{ filter: "drop-shadow(0 1px 0 rgba(168, 132, 61, 0.35))" }}
          />
          <span className="meta navbar-est hidden md:inline">EST · 2025</span>
        </a>

        {/* Centre wordmark */}
        <a href="/" onClick={goHome} className="display navbar-wordmark flex-1 text-center select-none">
          <span className="navbar-wordmark-row">
            <IndianFlag side="left" />
            <span className="navbar-wordmark-title">VILORA IMPEX</span>
            <IndianFlag side="right" />
          </span>
          <span className="navbar-wordmark-tagline" style={{
            display: "block",
            marginTop: 2,
            fontStyle: "italic",
            fontWeight: 300,
            fontSize: "clamp(13px, 1.35vw, 16px)",
            letterSpacing: "0.06em",
          }}>
            - Of grain, of spice, of trust -
          </span>
        </a>

        {/* Right - order/contact button */}
        <button
          onClick={() => go("inquiry")}
          className="btn-primary navbar-inquiry-btn hidden lg:inline-flex shrink-0"
        >
          <span>Place an Inquiry</span>
          <span className="btn-arrow">
            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
              <path d="M1 5h10M7 1l4 4-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square"/>
            </svg>
          </span>
        </button>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          className="navbar-menu-btn sm:hidden inline-flex items-center justify-center w-10 h-10 border border-current"
          style={{ display: "inline-flex", borderColor: "rgba(244, 239, 227, 0.42)", color: "var(--bone)" }}
        >
          <span className="relative w-4 h-4 block">
            <span className="absolute left-0 right-0 h-px bg-current transition-all duration-300"
                  style={{ top: open ? "50%" : "5px", transform: open ? "translateY(-50%) rotate(45deg)" : "none" }}/>
            <span className="absolute left-0 right-0 h-px bg-current transition-opacity duration-200"
                  style={{ top: "50%", transform: "translateY(-50%)", opacity: open ? 0 : 1 }}/>
            <span className="absolute left-0 right-0 h-px bg-current transition-all duration-300"
                  style={{ bottom: open ? "auto" : "5px", top: open ? "50%" : "auto",
                           transform: open ? "translateY(-50%) rotate(-45deg)" : "none" }}/>
          </span>
        </button>
      </div>

      <div className="rule mx-6 sm:mx-10" />

      {/* Nav row - desktop. Each link is a real chip-button (see .nav-chip
          in ledger.css) with a vermilion top-tab indicator that grows on
          hover, a bone-fill background tween, and a brass italic numeral. */}
      <nav className="navbar-nav-row hidden sm:flex items-center justify-center gap-1 md:gap-1.5 px-6 sm:px-10 py-4 mono"
           style={{ fontSize: "clamp(12px, 0.9vw, 14px)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
        {NAV.map((n, i) => (
          <button
            key={n.id}
            onClick={() => go(n)}
            className="nav-chip"
            aria-current={n.route === location.pathname ? "page" : undefined}
          >
            <span className="nav-chip-tab" aria-hidden />
            <span className="nav-chip-num">{String(i + 1).padStart(2, "0")}</span>
            <span className="nav-chip-label">{n.label}</span>
          </button>
        ))}
      </nav>

      {/* Mobile menu drawer */}
      {open && (
        <div
          className="mobile-nav-drawer sm:hidden fixed inset-0 z-50 paper"
          style={{ background: "var(--bone)" }}
        >
          <div className="mobile-nav-head">
            <div>
              <span className="folio">MENU</span>
              <div className="display-italic mobile-nav-kicker">Vilora Impex</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="mobile-nav-close"
            >
              ×
            </button>
          </div>

          <nav className="mobile-nav-list">
            {NAV.map((n, i) => (
              <button
                key={n.id}
                onClick={() => go(n)}
                className="mobile-nav-link"
                aria-current={n.route === location.pathname ? "page" : undefined}
              >
                <span className="folio">{String(i + 1).padStart(2, "0")}</span>
                <span className="display mobile-nav-label">{n.label}</span>
              </button>
            ))}
          </nav>

          <div className="mobile-nav-action">
            <CatalogueCTA variant="light" code="EXPORT" className="mobile-nav-catalogue" />
            <button onClick={() => go("inquiry")} className="btn-primary justify-center">
              <span>Place an Inquiry</span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

/**
 * Tiranga - proper Indian national flag.
 *
 * Spec: 3:2 aspect, three equal horizontal bands (saffron #FF9933,
 * white #FFFFFF, India green #138808). Ashoka Chakra is navy #000080,
 * diameter = 3/4 of the white band's height, with EXACTLY 24 evenly-
 * spaced spokes (representing the 24 hours of the day / 24 dharma
 * principles). Drawn as an SVG so the spokes are crisp at every size.
 */
function IndianFlag({ side }) {
  // 100x60 viewBox = 5:3 (close enough to 3:2 for the navbar size).
  // White band spans y=20 to y=40 (height 20). Chakra radius = 7.5 (3/4 of
  // half the band height = 7.5). Center: (50, 30).
  const CX = 50;
  const CY = 30;
  const R_OUTER = 7.5;
  const R_HUB = 1.4;
  const SPOKES = Array.from({ length: 24 }, (_, i) => i * 15); // 360 / 24

  return (
    <span
      className={`india-flag india-flag--${side}`}
      aria-label="Flag of India"
      role="img"
    >
      <svg
        viewBox="0 0 100 60"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block" }}
      >
        {/* Three bands */}
        <rect x="0" y="0"  width="100" height="20" fill="#FF9933" />
        <rect x="0" y="20" width="100" height="20" fill="#FFFFFF" />
        <rect x="0" y="40" width="100" height="20" fill="#138808" />

        {/* Ashoka Chakra */}
        <g
          stroke="#000080"
          fill="none"
          strokeWidth="0.6"
          strokeLinecap="round"
        >
          {/* Outer rim */}
          <circle cx={CX} cy={CY} r={R_OUTER} strokeWidth="0.7" />
          {/* Inner reference rim (decorative) */}
          <circle cx={CX} cy={CY} r={R_OUTER - 0.4} strokeWidth="0.25" opacity="0.5" />

          {/* 24 spokes - full diameter, navy */}
          {SPOKES.map((deg) => (
            <line
              key={`spoke-${deg}`}
              x1={CX}
              y1={CY - R_OUTER + 0.2}
              x2={CX}
              y2={CY + R_OUTER - 0.2}
              transform={`rotate(${deg} ${CX} ${CY})`}
              strokeWidth="0.45"
            />
          ))}
        </g>

        {/* Hub */}
        <circle cx={CX} cy={CY} r={R_HUB} fill="#000080" />
      </svg>
    </span>
  );
}
